import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { AnimatePresence } from 'framer-motion';
import { useAppDispatch, useAppSelector } from '@/store/reduxHooks';
import {
  clearToasts,
  dismissToast,
  setExpanded
} from '@/store/notifications';
import { isOrderNotification } from '@/utils/isOrderNotification';
import { OrderToastCard } from './components/OrderToastCard/OrderToastCard';
import { CollapsedOrdersToast } from './components/CollapsedOrdersToast/CollapsedOrdersToast';
import styles from './orderToastStack.module.scss';

const COLLAPSE_THRESHOLD = 2;

/**
 * Контейнер тостов о новых заказах. Подписан на эфемерный слой тостов слайса
 * notifications и сам решает форму отображения: до 2 уведомлений — отдельные
 * карточки, больше 2 — один схлопнутый тост со сводкой и раскрытием по клику.
 */
export const OrderToastStack: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const queue = useAppSelector((state) => state.notifications.toasts);
  const isExpanded = useAppSelector(
    (state) => state.notifications.isExpanded
  );

  const handleNavigate = useCallback(
    (path: string) => {
      void navigate(path);
    },
    [navigate]
  );

  const handleClose = useCallback(
    (id: string) => {
      dispatch(dismissToast(id));
    },
    [dispatch]
  );

  const handleCloseAll = useCallback(() => {
    dispatch(clearToasts());
  }, [dispatch]);

  const handleToggle = useCallback(
    (expanded: boolean) => {
      dispatch(setExpanded(expanded));
    },
    [dispatch]
  );

  const orderToasts = queue.filter(isOrderNotification);

  if (orderToasts.length === 0) {
    return null;
  }

  const isCollapsedView = orderToasts.length > COLLAPSE_THRESHOLD;

  return (
    <div
      className={styles['order-toast-stack']}
      role="status"
      aria-live="polite"
    >
      <AnimatePresence mode="popLayout" initial={false}>
        {isCollapsedView ? (
          <CollapsedOrdersToast
            key="collapsed"
            notifications={orderToasts}
            isExpanded={isExpanded}
            onToggle={handleToggle}
            onNavigate={handleNavigate}
            onClose={handleClose}
            onCloseAll={handleCloseAll}
          />
        ) : (
          orderToasts.map((notification) => (
            <OrderToastCard
              key={notification.id}
              notification={notification}
              onNavigate={handleNavigate}
              onClose={handleClose}
            />
          ))
        )}
      </AnimatePresence>
    </div>
  );
};
